/**
 * AddEditModal Component
 *
 * A comprehensive modal for creating and editing items of all types.
 * Dynamically renders form fields based on the item type.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { createPlainTextPreview, normalizeUrl, isUrl } from '@/utils/search';
import { fetchMetadata, getDomain } from '@/services/metadata';
import { useData } from '@/context/DataContext';
import { Button } from '@/components/ui/button';
import { InputWithLabel } from '@/components/ui/input';
import { TextareaWithLabel } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { TagInput } from '@/components/TagInput';
import { RichTextEditor } from '@/components/RichTextEditor';
import { useVoiceRecognition } from '@/hooks/useVoiceRecognition';
import { cn } from '@/utils/cn';
import type {
  Item,
  ItemType,
  LinkItem,
  LinkMetadata,
  PodcastItem,
  NoteItem,
  IdeaItem,
  FileItem,
  TaskItem,
} from '@/types';

// ============================================
// TYPES
// ============================================

export interface AddEditModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Modal mode */
  mode: 'add' | 'edit';
  /** Item type (for add mode) */
  itemType?: ItemType | undefined;
  /** Existing item (for edit mode) */
  item?: Item | null | undefined;
  /** Prefilled tags */
  prefillTags?: string[] | undefined;
  /** Prefilled URL (for links/podcasts) */
  prefillUrl?: string | undefined;
  /** Prefilled title */
  prefillTitle?: string | undefined;
  /** Prefilled content (for notes/ideas) */
  prefillContent?: string | undefined;
  /** Auto-start voice recording when modal opens */
  autoStartVoice?: boolean | undefined;
  /** Close handler */
  onClose: () => void;
}

// Form state for each item type
interface BaseFormState {
  title: string;
  description: string;
  tags: string[];
  isFavorite: boolean;
}

interface LinkFormState extends BaseFormState {
  url: string;
  thumbnail: string | null;
  favicon: string | null;
  siteName: string | null;
  personalNotes: string;
}

interface PodcastFormState extends BaseFormState {
  url: string;
  thumbnail: string | null;
  podcastName: string | null;
  personalNotes: string;
}

interface NoteFormState extends BaseFormState {
  content: string;
}

interface IdeaFormState extends BaseFormState {
  content: string;
}

interface FileFormState extends BaseFormState {
  personalNotes: string;
  // File-specific fields are read-only
}

interface TaskFormState extends BaseFormState {
  isCompleted: boolean;
}

type FormState =
  | LinkFormState
  | PodcastFormState
  | NoteFormState
  | IdeaFormState
  | FileFormState
  | TaskFormState;

type LinkLikeFormState = LinkFormState | PodcastFormState;

// ============================================
// INITIAL STATES
// ============================================

function createInitialState(
  type: ItemType,
  prefills?: { tags?: string[]; url?: string; title?: string; content?: string }
): FormState {
  const base: BaseFormState = {
    title: prefills?.title ?? '',
    description: '',
    tags: prefills?.tags ?? ['inbox'],
    isFavorite: false,
  };

  switch (type) {
    case 'link':
      return {
        ...base,
        url: prefills?.url ?? '',
        thumbnail: null,
        favicon: null,
        siteName: null,
        personalNotes: '',
      } as LinkFormState;
    case 'podcast':
      return {
        ...base,
        url: prefills?.url ?? '',
        thumbnail: null,
        podcastName: null,
        personalNotes: '',
      } as PodcastFormState;
    case 'note':
      return {
        ...base,
        content: prefills?.content ?? '',
      } as NoteFormState;
    case 'idea':
      return {
        ...base,
        content: prefills?.content ?? '',
      } as IdeaFormState;
    case 'file':
      return {
        ...base,
        personalNotes: '',
      } as FileFormState;
    case 'task':
      return {
        ...base,
        isCompleted: false,
      } as TaskFormState;
  }
}

function itemToFormState(item: Item): FormState {
  const base: BaseFormState = {
    title: item.title,
    description: item.description,
    tags: [...item.tags],
    isFavorite: item.isFavorite,
  };

  switch (item.type) {
    case 'link': {
      const link = item;
      return {
        ...base,
        url: link.url,
        thumbnail: link.thumbnail,
        favicon: link.favicon,
        siteName: link.siteName,
        personalNotes: link.personalNotes,
      } as LinkFormState;
    }
    case 'podcast': {
      const podcast = item;
      return {
        ...base,
        url: podcast.url,
        thumbnail: podcast.thumbnail,
        podcastName: podcast.podcastName,
        personalNotes: podcast.personalNotes,
      } as PodcastFormState;
    }
    case 'note': {
      const note = item;
      return {
        ...base,
        content: note.content,
      } as NoteFormState;
    }
    case 'idea': {
      const idea = item;
      return {
        ...base,
        content: idea.content,
      } as IdeaFormState;
    }
    case 'file': {
      const file = item;
      return {
        ...base,
        personalNotes: file.personalNotes,
      } as FileFormState;
    }
    case 'task': {
      const task = item;
      return {
        ...base,
        isCompleted: task.isCompleted,
      } as TaskFormState;
    }
  }
}

function isMetadataSupportedType(type: ItemType): type is 'link' | 'podcast' {
  return type === 'link' || type === 'podcast';
}

function mergeMetadataIntoState<T extends LinkLikeFormState>(
  state: T,
  type: 'link' | 'podcast',
  normalizedUrl: string,
  metadata: LinkMetadata
): T {
  const base = {
    ...state,
    url: normalizedUrl,
    title: state.title || metadata.title || '',
    description: state.description || metadata.description || '',
    thumbnail: metadata.image,
  };

  if (type === 'link') {
    return {
      ...base,
      favicon: metadata.favicon,
      siteName: metadata.siteName,
    } as T;
  }

  return {
    ...base,
    podcastName: metadata.siteName,
  } as T;
}

// ============================================
// MAIN COMPONENT
// ============================================

export function AddEditModal({
  isOpen,
  mode,
  itemType,
  item,
  prefillTags,
  prefillUrl,
  prefillTitle,
  prefillContent,
  autoStartVoice,
  onClose,
}: AddEditModalProps) {
  // Get data context
  const { createItem, updateItem, config } = useData();

  // Determine the actual item type
  const type: ItemType = mode === 'edit' && item ? item.type : (itemType ?? 'note');

  // Form state
  const [formState, setFormState] = useState<FormState>(() =>
    mode === 'edit' && item
      ? itemToFormState(item)
      : createInitialState(type, {
          tags: prefillTags,
          url: prefillUrl,
          title: prefillTitle,
          content: prefillContent,
        })
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fetchedMetadataUrlRef = useRef<string | null>(null);
  const pendingMetadataUrlRef = useRef<string | null>(null);
  const pendingMetadataPromiseRef = useRef<Promise<LinkMetadata> | null>(null);

  // Voice recording (for notes and ideas)
  const voice = useVoiceRecognition();
  const insertedLengthRef = useRef(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval>>();

  // Track recording duration
  useEffect(() => {
    if (voice.isRecording) {
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [voice.isRecording]);

  // Append finalized transcript to content in real-time
  useEffect(() => {
    if (
      (type !== 'note' && type !== 'idea') ||
      voice.finalTranscript.length <= insertedLengthRef.current
    ) {
      return;
    }

    const newText = voice.finalTranscript.slice(insertedLengthRef.current).trim();
    if (!newText) return;

    insertedLengthRef.current = voice.finalTranscript.length;

    setFormState((prev) => {
      const contentState = prev as NoteFormState | IdeaFormState;
      const current = contentState.content;

      let updated: string;
      if (!current || current === '<p></p>') {
        updated = `<p>${newText}</p>`;
      } else {
        // Append inside the last closing </p> tag
        const match = current.match(/<\/p>\s*$/);
        if (match) {
          updated = current.slice(0, -match[0].length) + ` ${newText}</p>`;
        } else {
          updated = current + `<p>${newText}</p>`;
        }
      }

      return { ...prev, content: updated };
    });
  }, [voice.finalTranscript, type]);

  // Reset voice and inserted length when modal reopens
  useEffect(() => {
    if (isOpen) {
      insertedLengthRef.current = 0;
    }
  }, [isOpen]);

  // Auto-start voice recording when opened from QuickAddBar mic button
  useEffect(() => {
    if (!isOpen || !autoStartVoice || !voice.isSupported || (type !== 'note' && type !== 'idea')) {
      return;
    }
    // Small delay to let the modal render and browser be ready
    const timer = setTimeout(() => voice.start(), 300);
    return () => clearTimeout(timer);
  }, [isOpen, autoStartVoice, voice.isSupported, type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Available tags for autocomplete
  const availableTags = useMemo(() => config.tags.map((t) => t.name), [config.tags]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormState(
        mode === 'edit' && item
          ? itemToFormState(item)
          : createInitialState(type, {
              tags: prefillTags,
              url: prefillUrl,
              title: prefillTitle,
              content: prefillContent,
            })
      );
      setErrors({});
      fetchedMetadataUrlRef.current = null;
      pendingMetadataUrlRef.current = null;
      pendingMetadataPromiseRef.current = null;
    }
  }, [isOpen, mode, item, type, prefillTags, prefillUrl, prefillTitle, prefillContent]);

  // Update form field
  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      // Clear error when field is updated
      if (errors[field as string]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field as string];
          return next;
        });
      }
    },
    [errors]
  );

  const hydrateMetadata = useCallback(
    async <T extends LinkLikeFormState>(state: T, applyToForm: boolean): Promise<T> => {
      if (mode !== 'add' || !isMetadataSupportedType(type)) return state;

      const url = state.url.trim();
      if (!url || !isUrl(url)) return state;

      const normalizedUrl = normalizeUrl(url);
      const normalizedState =
        normalizedUrl === state.url ? state : ({ ...state, url: normalizedUrl } as T);

      if (fetchedMetadataUrlRef.current === normalizedUrl) {
        return normalizedState;
      }

      let metadataPromise = pendingMetadataPromiseRef.current;
      if (!metadataPromise || pendingMetadataUrlRef.current !== normalizedUrl) {
        metadataPromise = fetchMetadata(normalizedUrl);
        pendingMetadataUrlRef.current = normalizedUrl;
        pendingMetadataPromiseRef.current = metadataPromise;
      }

      setIsFetchingMetadata(true);

      try {
        const metadata = await metadataPromise;
        const nextState = mergeMetadataIntoState(normalizedState, type, normalizedUrl, metadata);

        if (applyToForm) {
          setFormState((prev) => {
            if (!isMetadataSupportedType(type)) return prev;

            const prevState = prev as LinkLikeFormState;
            const prevUrl = prevState.url.trim();
            if (!prevUrl || !isUrl(prevUrl)) return prev;
            if (normalizeUrl(prevUrl) !== normalizedUrl) return prev;

            fetchedMetadataUrlRef.current = normalizedUrl;
            return mergeMetadataIntoState(prevState, type, normalizedUrl, metadata) as FormState;
          });
        } else {
          fetchedMetadataUrlRef.current = normalizedUrl;
        }

        return nextState;
      } catch (err) {
        console.error('Failed to fetch metadata:', err);
        return normalizedState;
      } finally {
        if (pendingMetadataUrlRef.current === normalizedUrl) {
          pendingMetadataUrlRef.current = null;
          pendingMetadataPromiseRef.current = null;
        }
        setIsFetchingMetadata(false);
      }
    },
    [mode, type]
  );

  // Fetch metadata for prefills when the add modal opens.
  useEffect(() => {
    if (!isOpen || mode !== 'add' || !isMetadataSupportedType(type) || !prefillUrl) return;

    const urlState = formState as LinkLikeFormState;
    const url = urlState.url.trim();
    if (!url || !isUrl(url)) return;

    void hydrateMetadata(urlState, true);
  }, [isOpen, mode, type, prefillUrl, formState, hydrateMetadata]);

  // Fetch metadata for URLs on blur (only in add mode)
  const handleUrlBlur = useCallback(() => {
    if (mode !== 'add' || !isMetadataSupportedType(type)) return;
    void hydrateMetadata(formState as LinkLikeFormState, true);
  }, [mode, type, formState, hydrateMetadata]);

  // Validate form
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Title required for all types EXCEPT links and podcasts (they use URL domain as fallback)
    if (!formState.title.trim() && type !== 'link' && type !== 'podcast') {
      newErrors.title = 'Title is required';
    }

    // URL required for links and podcasts
    if (type === 'link' || type === 'podcast') {
      const urlState = formState as LinkFormState | PodcastFormState;
      if (!urlState.url.trim()) {
        newErrors.url = 'URL is required';
      } else if (!isUrl(urlState.url)) {
        newErrors.url = 'Please enter a valid URL';
      }
    }

    // Content required for notes and ideas
    if (type === 'note' || type === 'idea') {
      const contentState = formState as NoteFormState | IdeaFormState;
      if (!contentState.content.trim() || contentState.content === '<p></p>') {
        newErrors.content = 'Content is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState, type]);

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;

    setIsLoading(true);
    try {
      if (mode === 'add') {
        // Create new item - use type assertion since we know the exact shape for each case
        switch (type) {
          case 'link': {
            const state = await hydrateMetadata(formState as LinkFormState, false);
            const normalizedUrl = normalizeUrl(state.url);
            await createItem({
              type: 'link',
              title: state.title.trim() || getDomain(normalizedUrl) || normalizedUrl,
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              url: normalizedUrl,
              thumbnail: state.thumbnail,
              favicon: state.favicon,
              siteName: state.siteName,
              personalNotes: state.personalNotes.trim(),
            } as Omit<LinkItem, 'id' | 'createdAt' | 'updatedAt'>);
            break;
          }
          case 'podcast': {
            const state = await hydrateMetadata(formState as PodcastFormState, false);
            const normalizedUrl = normalizeUrl(state.url);
            await createItem({
              type: 'podcast',
              title: state.title.trim() || getDomain(normalizedUrl) || normalizedUrl,
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              url: normalizedUrl,
              thumbnail: state.thumbnail,
              podcastName: state.podcastName,
              personalNotes: state.personalNotes.trim(),
            } as Omit<PodcastItem, 'id' | 'createdAt' | 'updatedAt'>);
            break;
          }
          case 'note': {
            const state = formState as NoteFormState;
            await createItem({
              type: 'note',
              title: state.title.trim(),
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              content: state.content,
              plainTextPreview: createPlainTextPreview(state.content),
            } as Omit<NoteItem, 'id' | 'createdAt' | 'updatedAt'>);
            break;
          }
          case 'idea': {
            const state = formState as IdeaFormState;
            await createItem({
              type: 'idea',
              title: state.title.trim(),
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              content: state.content,
              plainTextPreview: createPlainTextPreview(state.content),
            } as Omit<IdeaItem, 'id' | 'createdAt' | 'updatedAt'>);
            break;
          }
          case 'task': {
            const state = formState as TaskFormState;
            await createItem({
              type: 'task',
              title: state.title.trim(),
              description: '',
              tags: state.tags,
              isFavorite: state.isFavorite,
              isCompleted: state.isCompleted,
            } as Omit<TaskItem, 'id' | 'createdAt' | 'updatedAt'>);
            break;
          }
          default:
            throw new Error(`Unsupported type: ${type}`);
        }
      } else if (item) {
        // Update existing item
        let updatedItem: Item;

        switch (type) {
          case 'link': {
            const state = formState as LinkFormState;
            const normalizedUrl = normalizeUrl(state.url);
            updatedItem = {
              ...item,
              title: state.title.trim() || getDomain(normalizedUrl) || normalizedUrl,
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              url: normalizedUrl,
              thumbnail: state.thumbnail,
              favicon: state.favicon,
              siteName: state.siteName,
              personalNotes: state.personalNotes.trim(),
            } as LinkItem;
            break;
          }
          case 'podcast': {
            const state = formState as PodcastFormState;
            const normalizedUrl = normalizeUrl(state.url);
            updatedItem = {
              ...item,
              title: state.title.trim() || getDomain(normalizedUrl) || normalizedUrl,
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              url: normalizedUrl,
              thumbnail: state.thumbnail,
              podcastName: state.podcastName,
              personalNotes: state.personalNotes.trim(),
            } as PodcastItem;
            break;
          }
          case 'note': {
            const state = formState as NoteFormState;
            updatedItem = {
              ...item,
              title: state.title.trim(),
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              content: state.content,
              plainTextPreview: createPlainTextPreview(state.content),
            } as NoteItem;
            break;
          }
          case 'idea': {
            const state = formState as IdeaFormState;
            updatedItem = {
              ...item,
              title: state.title.trim(),
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              content: state.content,
              plainTextPreview: createPlainTextPreview(state.content),
            } as IdeaItem;
            break;
          }
          case 'file': {
            const state = formState as FileFormState;
            updatedItem = {
              ...item,
              title: state.title.trim(),
              description: state.description.trim(),
              tags: state.tags,
              isFavorite: state.isFavorite,
              personalNotes: state.personalNotes.trim(),
            } as FileItem;
            break;
          }
          case 'task': {
            const state = formState as TaskFormState;
            updatedItem = {
              ...item,
              title: state.title.trim(),
              description: '',
              tags: state.tags,
              isFavorite: state.isFavorite,
              isCompleted: state.isCompleted,
            } as TaskItem;
            break;
          }
          default:
            throw new Error(`Unsupported type: ${type}`);
        }

        updateItem(updatedItem);
      }

      onClose();
    } catch (err) {
      console.error('Failed to save item:', err);
      setErrors({ submit: 'Failed to save. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Modal title
  const title =
    mode === 'add'
      ? `Add ${type.charAt(0).toUpperCase() + type.slice(1)}`
      : `Edit ${type.charAt(0).toUpperCase() + type.slice(1)}`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 overflow-y-auto space-y-4">
          {/* URL field (links and podcasts) */}
          {(type === 'link' || type === 'podcast') && (
            <div className="relative">
              <InputWithLabel
                id="url"
                label="URL"
                type="url"
                value={(formState as LinkFormState | PodcastFormState).url}
                onChange={(e) =>
                  updateField(
                    'url' as keyof FormState,
                    e.target.value as FormState[keyof FormState]
                  )
                }
                onBlur={handleUrlBlur}
                placeholder="https://example.com"
                error={!!errors.url}
                errorMessage={errors.url}
                disabled={isFetchingMetadata}
              />
              {isFetchingMetadata && (
                <div className="absolute right-3 top-8">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>
          )}

          {/* Title field */}
          <InputWithLabel
            id="title"
            label="Title"
            value={formState.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Enter a title"
            error={!!errors.title}
            errorMessage={errors.title}
            autoFocus={mode === 'add'}
          />

          {/* Description field (not for tasks, notes, or ideas) */}
          {type !== 'task' && type !== 'note' && type !== 'idea' && (
            <TextareaWithLabel
              id="description"
              label="Description"
              value={formState.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Add a brief description (optional)"
              className="min-h-[80px]"
            />
          )}

          {/* Content field (notes and ideas) */}
          {(type === 'note' || type === 'idea') && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Content</label>
                {voice.isSupported && (
                  <div className="flex items-center gap-2">
                    {voice.isRecording && (
                      <span className="flex items-center gap-1.5 text-xs text-red-600">
                        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                        {Math.floor(recordingDuration / 60)}:
                        {(recordingDuration % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={voice.isRecording ? voice.stop : voice.start}
                      className={cn(
                        'p-1.5 rounded transition-colors',
                        voice.isRecording
                          ? 'text-red-600 bg-red-50 hover:bg-red-100'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      )}
                      title={
                        voice.isRecording
                          ? 'Stop recording'
                          : "Start voice recording — uses your browser's speech recognition"
                      }
                    >
                      {voice.isRecording ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}
              </div>
              <RichTextEditor
                content={(formState as NoteFormState | IdeaFormState).content}
                onChange={(html) =>
                  updateField('content' as keyof FormState, html as FormState[keyof FormState])
                }
                placeholder={type === 'note' ? 'Write your note...' : 'Capture your idea...'}
                minHeight="150px"
                autofocus={false}
                onSubmit={() => void handleSave()}
              />
              {voice.interimTranscript && (
                <p className="text-sm text-gray-400 italic">{voice.interimTranscript}</p>
              )}
              {voice.error && <p className="text-sm text-amber-600">{voice.error}</p>}
              {voice.isRecording && (
                <p className="text-xs text-gray-400">
                  Voice uses your browser&apos;s speech recognition.
                </p>
              )}
              {errors.content && <p className="text-sm text-red-600">{errors.content}</p>}
            </div>
          )}

          {/* Personal notes field (links, podcasts, files) */}
          {(type === 'link' || type === 'podcast' || type === 'file') && (
            <TextareaWithLabel
              id="personalNotes"
              label="Personal Notes"
              value={(formState as LinkFormState | PodcastFormState | FileFormState).personalNotes}
              onChange={(e) =>
                updateField(
                  'personalNotes' as keyof FormState,
                  e.target.value as FormState[keyof FormState]
                )
              }
              placeholder="Add your personal notes..."
              className="min-h-[80px]"
            />
          )}

          {/* Tags */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Tags</label>
            <TagInput
              tags={formState.tags}
              onChange={(tags) => updateField('tags', tags)}
              availableTags={availableTags}
              placeholder="Add tags..."
            />
          </div>

          {/* Error message */}
          {errors.submit && <p className="text-sm text-red-600">{errors.submit}</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : mode === 'add' ? (
              'Create'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
